import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import { TiltCard } from "../components/TiltCard";
import { PROJECTS } from "../data/mock";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const ResultRow = ({ r }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#6C0B1B]/8 last:border-0">
    <p className="text-[13px] text-[#5C4B51]">{r.metric}</p>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-[13px] text-[#5C4B51]/70 line-through decoration-[#A31621]/40">{r.before}</span>
      <ArrowRight size={13} className="text-[#A31621]" />
      <span className="text-sm font-bold text-[#6C0B1B]">{r.after}</span>
    </div>
  </div>
);

export default function Portfolio() {
  return (
    <div data-testid="portfolio-page" className="pt-16">
      <section className="bg-[#FDFBF7] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-4">Portfolio</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1014]">
              Real clients. Real <span className="italic text-[#6C0B1B]">before &amp; after.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C4B51] leading-relaxed">
              We don't sell promises — we show numbers. Here's what happened when these businesses partnered with Levaze.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PROJECTS.map((p, i) => (
              <motion.div key={p.client} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 2) * 0.1 }}>
                <Link to={`/portfolio/${p.slug}`} data-testid={`portfolio-link-${i}`} className="block h-full">
                  <TiltCard testId={`portfolio-card-${i}`}
                  className="group bg-white border border-[#6C0B1B]/10 rounded-2xl overflow-hidden h-full shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)] hover:border-[#6C0B1B]/30 transition-colors duration-300">
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.image} alt={p.client}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 rounded-full bg-[#6C0B1B] text-white text-xs font-medium px-3.5 py-1.5">
                      {p.service}
                    </span>
                    <span className="absolute top-4 right-4 rounded-full bg-[#FDFBF7]/90 backdrop-blur text-[#1A1014] text-xs font-medium px-3.5 py-1.5 flex items-center gap-1.5">
                      <Clock size={12} className="text-[#A31621]" /> {p.duration}
                    </span>
                  </div>
                  <div className="p-7">
                    <p className="text-xs text-[#A31621] font-semibold uppercase tracking-wider">{p.industry}</p>
                    <h3 className="mt-1.5 font-heading text-3xl font-semibold text-[#1A1014]">{p.client}</h3>
                    <p className="mt-3 text-[15px] text-[#5C4B51] leading-relaxed">{p.summary}</p>
                    <div className="mt-5 rounded-xl bg-[#F3EFE9] px-5 py-3">
                      <div className="flex items-center justify-between pb-1 pt-1">
                        <p className="uppercase text-[10px] tracking-[0.2em] text-[#5C4B51] font-semibold">Results</p>
                        <p className="uppercase text-[10px] tracking-[0.2em] text-[#5C4B51] font-semibold">Before → After</p>
                      </div>
                      {p.results.map((r) => <ResultRow key={r.metric} r={r} />)}
                    </div>
                    <p className="mt-5 inline-flex items-center gap-1.5 text-[#6C0B1B] font-medium text-sm group-hover:gap-3 transition-[gap] duration-200">
                      View full case study <ArrowUpRight size={15} />
                    </p>
                  </div>
                  </TiltCard>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-16 rounded-2xl bg-[#6C0B1B] text-white p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light">Your business could be the next case study.</h2>
              <p className="mt-2 text-white/70">Tell us where you are — we'll show you where you can be.</p>
            </div>
            <Link to="/contact" data-testid="portfolio-contact-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#6C0B1B] px-7 py-3.5 font-medium hover:bg-[#F3EFE9] transition-colors duration-200 shrink-0">
              Start Your Story <ArrowUpRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
